import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { Autocomplete, Chip, TextField, useMediaQuery, useTheme } from "@mui/material";
import { useContext } from "react";
import { UserContext } from "../../hooks/UserContext";
import { useCompanyContext } from "../../hooks/CompanyContext";
import { useImpersonationContext } from "../../hooks/ImpersonationContext";
import { useImpersonationSelector } from "./useImpersonationSelector";
import type { ImpersonatableUser } from "../../api/routers/users";

interface ButtonAppBarProps {
	title: string;
	onMenuClick?: () => void;
}

export function CustomToolbar(props: ButtonAppBarProps) {
	const theme = useTheme();
	const user = useContext(UserContext);
	const { companies, selectedCompany, setSelectedCompany } = useCompanyContext();

	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const showCompanySelector = user?.isAdmin && companies.length > 1;
	const { isImpersonating } = useImpersonationContext();
	const showImpersonationSelector = user?.isDeveloper || isImpersonating;
	const { impersonatedEmail, startImpersonation, stopImpersonation, users, isLoading, fetchUsers } =
		useImpersonationSelector(selectedCompany?.id, showImpersonationSelector);

	return (
		<Box style={{ zIndex: theme.zIndex.drawer + 1 }} sx={{ flexGrow: 1 }}>
			<AppBar position="static">
				<Toolbar>
					{isMobile && (
						<IconButton
							size="large"
							edge="start"
							color="inherit"
							aria-label="menu"
							onClick={props.onMenuClick}
							sx={{ mr: 2 }}
						>
							<MenuIcon />
						</IconButton>
					)}
					<Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
						{props.title}
					</Typography>
					{showCompanySelector && (
						<Autocomplete
							size="small"
							options={companies}
							value={selectedCompany}
							getOptionKey={(option) => option.id}
							getOptionLabel={(option) => option.name}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							onChange={(_, value) => setSelectedCompany(value)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Company"
									variant="outlined"
									size="small"
									sx={{
										"& .MuiOutlinedInput-root": {
											color: "white",
											"& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
											"&:hover fieldset": { borderColor: "white" },
											"&.Mui-focused fieldset": { borderColor: "white" },
										},
										"& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
										"& .MuiInputLabel-root.Mui-focused": { color: "white" },
										"& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
									}}
								/>
							)}
							sx={{ minWidth: { xs: 150, sm: 220 }, maxWidth: 300, flexShrink: 1 }}
						/>
					)}
					{showImpersonationSelector && isImpersonating && (
						<Chip
							label={`Impersonating: ${impersonatedEmail}`}
							color="warning"
							size="small"
							onDelete={stopImpersonation}
							sx={{ ml: 1, fontWeight: "bold" }}
						/>
					)}
					{user?.isDeveloper && (
						<Autocomplete<ImpersonatableUser>
							size="small"
							options={users}
							loading={isLoading}
							value={
								impersonatedEmail
									? (users.find((u) => u.email === impersonatedEmail) ?? {
											id: impersonatedEmail,
											email: impersonatedEmail,
											name: impersonatedEmail,
											companyIds: [],
										})
									: null
							}
							getOptionKey={(option) => option.id}
							getOptionLabel={(option) => `${option.name} (${option.email})`}
							isOptionEqualToValue={(option, value) => option.email === value.email}
							onOpen={fetchUsers}
							onChange={(_, value) => {
								if (value) {
									startImpersonation(value.email);
								} else {
									stopImpersonation();
								}
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Impersonate User"
									variant="outlined"
									size="small"
									sx={{
										"& .MuiOutlinedInput-root": {
											color: "white",
											"& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
											"&:hover fieldset": { borderColor: "white" },
											"&.Mui-focused fieldset": { borderColor: "white" },
										},
										"& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
										"& .MuiInputLabel-root.Mui-focused": { color: "white" },
										"& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
									}}
								/>
							)}
							sx={{ minWidth: { xs: 150, sm: 250 }, maxWidth: 350, flexShrink: 1, ml: 1 }}
						/>
					)}
				</Toolbar>
			</AppBar>
		</Box>
	);
}
